import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AgentGuideProfile.css";

import {
  FaEdit,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaCoins,
  FaUser,
  FaPlus,
  FaArrowRight,
  FaCalendarAlt,
  FaHistory,
  FaStar,
  FaGlobe,
  FaWalking,
  FaInfoCircle,
  FaCalendarCheck,
  FaLanguage
} from "react-icons/fa";
import NavUser from "../components/NavUser";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate, useParams } from "react-router-dom";
import { getFileUrl } from "../utils/imageUrl";

function AgentGuideProfile() {
  const navigate = useNavigate();
  const { guideId, agentDetailsId } = useParams();

  const [guide, setGuide] = useState(null);
  const [tours, setTours] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [loading, setLoading] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  const [stats, setStats] = useState({
    activeListings: 0,
    bookingsThisWeek: 0,
    totalEarning: 0,
    coinBalance: 0
  });

  const [lastWithdrawal, setLastWithdrawal] = useState({
    amount: 0,
    date: null,
    reference_number: null
  });

  // OPEN BOOKING MODAL
  const openBookingModal = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const closeBookingModal = () => {
    setSelectedBooking(null);
    setIsModalOpen(false);
  };


  useEffect(() => {
    async function loadGuideData() {
      try {
        // Load guide basic details
        const res = await axios.get(`${BACKEND_URL}/guide/${guideId}`);
        setGuide(res.data.guide);
      } catch (e) {
        console.error("ERROR LOADING CRITICAL GUIDE PROFILE DATA:", e);
        setLoading(false);
        return; // Stop if we can't even load the guide
      }

      let realGuideId;
      try {
        // Get the real guide ID using the user ID
        const guideIdRes = await axios.get(
          `${BACKEND_URL}/guide/user/${guideId}/guide-id`
        );
        realGuideId = guideIdRes.data.guideId;
        console.log("Real Guide ID:", realGuideId);
      } catch (e) {
        console.error("Error loading real guide ID:", e);
      }

      if (realGuideId) {
        try {
          // Load agent-related tour listings using the real guide ID
          const listingRes = await axios.get(
            `${BACKEND_URL}/guides/agent/${agentDetailsId}/guide/${realGuideId}/listings`
          );
          setTours(listingRes.data.listings || []);
          setStats((prev) => ({
            ...prev,
            activeListings: listingRes.data.total_count || 0,
          }));
        } catch (e) { console.error("Error loading listings", e); }

        try {
          // Load bookings
          const bookingRes = await axios.get(
            `${BACKEND_URL}/guides/agent/${agentDetailsId}/guide/${realGuideId}/bookings`
          );
          setBookings(bookingRes.data.bookings || []);
        } catch (e) { console.error("Error loading bookings", e); }
      }

      try {
        // Load reviews
        const reviewRes = await axios.get(
          `${BACKEND_URL}/guide/${guideId}/reviews`
        );
        setReviews(reviewRes.data.reviews || []);
      } catch (e) { console.error("Error loading reviews", e); }

      try {
        // Load last withdrawal
        const withdrawalRes = await axios.get(
          `${BACKEND_URL}/guide/${guideId}/last-withdrawal`
        );
        if (withdrawalRes.data?.success) {
          setLastWithdrawal({
            amount: withdrawalRes.data.last_withdrawal_amount,
            date: withdrawalRes.data.last_withdrawal_date,
            reference_number: withdrawalRes.data.reference_number
          });
        }
      } catch (e) { console.error("Error loading last withdrawal", e); }

      try {
        // Load earnings
        const earningRes = await axios.get(
          `${BACKEND_URL}/agents/earnings/${agentDetailsId}`
        );
        setStats((prev) => ({
          ...prev,
          totalEarning: earningRes.data.earnings || 0,
        }));
      } catch (e) { console.error("Error loading earnings", e); }

      try {
        // Load coin balance
        const coinRes = await axios.get(
          `${BACKEND_URL}/coin-transactions/balance/${guideId}`
        );
        setStats((prev) => ({
          ...prev,
          coinBalance: coinRes.data.coin_balance || 0,
        }));
      } catch (e) { console.error("Error loading coin balance", e); }

      setLoading(false);
    }
    loadGuideData();
  }, [guideId, agentDetailsId, BACKEND_URL]);

  if (loading) return <p>Loading guide profile...</p>;
  if (!guide) return <p>Guide not found.</p>;

  // Calendar highlight logic
  const handleDateChange = (date) => {
    const dateStr = date.toDateString();

    if (availableDates.some(d => d.toDateString() === dateStr)) {
      setAvailableDates(availableDates.filter(d => d.toDateString() !== dateStr));
    } else {
      setAvailableDates([...availableDates, date]);
    }
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month" &&
      availableDates.some(d => d.toDateString() === date.toDateString())) {
      return "highlight-date";
    }
    return null;
  };

  return (
    <div className="agent-guide-dashboard">
      <NavUser />
      
      <div className="agent-dashboard-container">
        <div className="agent-dashboard-grid">
          
          {/* SIDEBAR */}
          <aside className="agent-dashboard-sidebar">
            
            {/* PROFILE CARD */}
            <div className="agent-sidebar-card">
              <div className="agent-avatar-container">
                {guide.logo ? (
                  <img
                    src={getFileUrl(guide.logo, BACKEND_URL)}
                    alt="Guide"
                    className="agent-avatar-img"
                  />
                ) : (
                  <div className="agent-avatar-placeholder" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f6eb5', color: '#fff', fontSize: '2.5rem', fontWeight: 'bold' }}>
                    {guide.full_name?.charAt(0) || "G"}
                  </div>
                )}
              </div>
              
              <div className="agent-profile-info">
                <h2 className="agent-guide-name">{guide.full_name}</h2>
                
                <div className="agent-guide-meta">
                  {guide.Gender?.name && (
                    <div className="agent-meta-item">
                      <FaUser className="agent-meta-icon" />
                      <span>{guide.Gender.name}</span>
                    </div>
                  )}
                  {guide.Country?.name && (
                    <div className="agent-meta-item">
                      <FaGlobe className="agent-meta-icon" />
                      <span>{guide.Country.name}</span>
                    </div>
                  )}
                  {guide.national_id_number && (
                    <div className="agent-meta-item">
                      <FaInfoCircle className="agent-meta-icon" />
                      <span>NIC: {guide.national_id_number}</span>
                    </div>
                  )}
                </div>
                
                <button
                  className="agent-edit-profile-btn"
                  onClick={() =>
                    navigate("/edit-profile", { state: { guide } })
                  }
                >
                  <FaEdit /> Edit Profile
                </button>
              </div>
            </div>

            {/* WITHDRAWAL SUMMARY */}
            <div className="agent-withdrawal-card">
              <h3 className="agent-withdrawal-title">
                <FaHistory /> Withdrawal Summary
              </h3>
              <div className="agent-withdrawal-metrics">
                <div className="agent-withdrawal-row">
                  <span className="agent-withdrawal-label">Total Withdrawn</span>
                  <span className="agent-withdrawal-value highlight">${lastWithdrawal.amount || 0}</span>
                </div>
              </div>
            </div>

            {/* AVAILABILITY CALENDAR */}
            <div className="agent-calendar-card">
              <h3 className="agent-calendar-title">
                <FaCalendarAlt /> Availability Calendar
              </h3>
              <Calendar
                onClickDay={handleDateChange}
                tileClassName={tileClassName}
              />
            </div>

          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="agent-dashboard-main">
            
            {/* QUICK STATS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaInfoCircle className="agent-section-title-icon" /> Quick Stats Summary
                </h3>
              </div>
              
              <div className="agent-stats-grid">
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaWalking />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Active Listings</span>
                    <p className="agent-stat-value">{stats.activeListings}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaCalendarCheck />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Pending Bookings</span>
                    <p className="agent-stat-value">{bookings.length}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaMoneyBillWave />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Total Earnings</span>
                    <p className="agent-stat-value">${stats.totalEarning}</p>
                  </div>
                </div>
                
                <div className="agent-stat-card">
                  <div className="agent-stat-icon-wrapper">
                    <FaCoins />
                  </div>
                  <div className="agent-stat-details">
                    <span className="agent-stat-label">Coin Balance</span>
                    <p className="agent-stat-value">{stats.coinBalance}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* TOUR LISTINGS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaWalking className="agent-section-title-icon" /> Tour Listings
                </h3>
                <button
                  className="agent-primary-btn"
                  onClick={() => navigate(`/tourform/${guide.id}`)}
                >
                  <FaPlus /> Add New Tour
                </button>
              </div>
              
              <div className="agent-tours-grid">
                {tours.length > 0 ? (
                  tours.map((tour) => (
                    <div key={tour.id} className="agent-tour-item">
                      <div>
                        <h4 className="agent-card-title">{tour.title}</h4>
                        <div className="agent-card-details">
                          <div className="agent-card-row">
                            <FaLanguage className="agent-card-icon" />
                            <span><strong>Type:</strong> {tour.tour_type?.name}</span>
                          </div>
                          <div className="agent-card-row">
                            <FaMapMarkerAlt className="agent-card-icon" />
                            <span>{tour.service_area}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="agent-card-btn"
                        onClick={() => navigate(`/tour/${tour.id}`)}
                      >
                        View Details
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="agent-empty-state">
                    <div className="agent-empty-icon">
                      <FaWalking />
                    </div>
                    <h4 className="agent-empty-title">No Tours Registered</h4>
                    <p className="agent-empty-desc">This guide doesn't have any registered tours yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* UPCOMING BOOKINGS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaCalendarAlt className="agent-section-title-icon" /> Upcoming Bookings ({bookings.length})
                </h3>
              </div>
              
              <div className="agent-booking-list">
                {bookings.length === 0 ? (
                  <div className="agent-empty-state">
                    <div className="agent-empty-icon">
                      <FaCalendarAlt />
                    </div>
                    <h4 className="agent-empty-title">No Bookings Found</h4>
                    <p className="agent-empty-desc">There are no upcoming bookings for this guide.</p>
                  </div>
                ) : (
                  bookings.map((booking) => (
                    <div key={booking.id} className="agent-booking-item">
                      <div>
                        <h4 className="agent-card-title">{booking.tour_listing?.title}</h4>
                        <div className="agent-card-details">
                          <div className="agent-card-row">
                            <FaUser className="agent-card-icon" />
                            <span><strong>Traveler:</strong> {booking.traveler?.full_name}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="agent-card-btn"
                        onClick={() => openBookingModal(booking)}
                      >
                        <FaInfoCircle /> View Details
                      </button>
                    </div>
                  ))
                )}
              </div>

              <button
                className="agent-view-all-btn"
                onClick={() => navigate(`/agent-tours/${agentDetailsId}/${guideId}/bookings`)}
              >
                View All Bookings <FaArrowRight />
              </button>
            </div>

            {/* TRAVELER REVIEWS */}
            <div className="agent-main-section">
              <div className="agent-section-header">
                <h3 className="agent-section-title">
                  <FaStar className="agent-section-title-icon" style={{ color: "#f59e0b" }} /> Traveler Reviews
                </h3>
              </div>
              
              <div className="agent-reviews-list">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review.id} className="agent-review-card">
                      <div className="agent-review-header">
                        <div className="agent-review-user-info">
                          <div className="agent-review-avatar-placeholder">
                            {review.user?.full_name?.charAt(0) || "T"}
                          </div>
                          <div>
                            <strong className="agent-review-author">{review.user?.full_name}</strong>
                          </div>
                        </div>
                        {review.rating && (
                          <div className="agent-review-rating">
                            {Array.from({ length: review.rating }).map((_, idx) => (
                              <FaStar key={idx} />
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="agent-review-comment">{review.comment}</p>
                    </div>
                  ))
                ) : (
                  <div className="agent-empty-state" style={{ border: "none", padding: "2rem" }}>
                    <div className="agent-empty-icon">
                      <FaStar />
                    </div>
                    <h4 className="agent-empty-title">No Reviews Yet</h4>
                    <p className="agent-empty-desc">This guide hasn't received any reviews yet.</p>
                  </div>
                )}
              </div>
            </div>

          </main>
        </div>
      </div>

      {/* BOOKING MODAL */}
      {isModalOpen && selectedBooking && (
        <div className="agent-modal-overlay" onClick={closeBookingModal}>
          <div className="agent-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="agent-modal-header">
              <h3>Booking Details</h3>
            </div>
            <div className="agent-modal-body">
              <h4 className="agent-modal-section-title">Tour Overview</h4>
              <div className="agent-detail-grid">
                <div className="agent-detail-item full-width">
                  <span className="agent-detail-label">Tour Title</span>
                  <span className="agent-detail-value">{selectedBooking.tour_listing?.title}</span>
                </div>
              </div>

              <h4 className="agent-modal-section-title">Traveler Information</h4>
              <div className="agent-detail-grid">
                <div className="agent-detail-item full-width">
                  <span className="agent-detail-label">Traveler Name</span>
                  <span className="agent-detail-value">{selectedBooking.traveler?.full_name}</span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Email Address</span>
                  <span className="agent-detail-value" style={{ fontSize: "0.85rem", wordBreak: "break-all" }}>
                    {selectedBooking.traveler?.email}
                  </span>
                </div>
                <div className="agent-detail-item">
                  <span className="agent-detail-label">Phone Number</span>
                  <span className="agent-detail-value">{selectedBooking.traveler?.phone_number || "N/A"}</span>
                </div>
              </div>
            </div>
            <div className="agent-modal-footer">
              <button className="agent-modal-close-btn" onClick={closeBookingModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AgentGuideProfile;
